/**
 * Cascade Engine — Computes time-phased degradation when assets are lost.
 *
 * Pure TypeScript, no backend calls needed except reusing existing /api/predict.
 */

import type { FusionCluster, AssetDetail } from '../types'

// ===== Impact Types =====
export type ImpactType =
  | 'fuel_denial'
  | 'isr_gap'
  | 'c2_degradation'
  | 'logistics_disruption'
  | 'kill_chain_break'
  | 'comms_degradation'
  | 'defensive_gap'
  | 'maintenance_cascade'

// ===== Time points (minutes) =====
export const TIME_POINTS = [0, 15, 30, 60, 120] as const
export type TimePoint = (typeof TIME_POINTS)[number]

export const PHASE_LABELS: Record<TimePoint, string> = {
  0: 'IMMEDIATE',
  15: 'SECONDARY',
  30: 'ADAPTATION',
  60: 'DEGRADATION',
  120: 'COLLAPSE',
}

// ===== Degradation curves by impact type =====
// Values are the remaining capacity multiplier at each time point
const DEGRADATION_CURVES: Record<ImpactType, Record<TimePoint, number>> = {
  fuel_denial:           { 0: 1.0,  15: 0.85, 30: 0.65, 60: 0.40, 120: 0.25 },
  isr_gap:               { 0: 0.70, 15: 0.55, 30: 0.40, 60: 0.35, 120: 0.30 },
  c2_degradation:        { 0: 0.60, 15: 0.50, 30: 0.45, 60: 0.40, 120: 0.35 },
  logistics_disruption:  { 0: 1.0,  15: 0.95, 30: 0.85, 60: 0.65, 120: 0.40 },
  kill_chain_break:      { 0: 0.50, 15: 0.40, 30: 0.35, 60: 0.30, 120: 0.25 },
  comms_degradation:     { 0: 0.75, 15: 0.65, 30: 0.55, 60: 0.45, 120: 0.35 },
  defensive_gap:         { 0: 0.80, 15: 0.70, 30: 0.60, 60: 0.50, 120: 0.40 },
  maintenance_cascade:   { 0: 1.0,  15: 0.90, 30: 0.80, 60: 0.65, 120: 0.50 },
}

// ===== Feature index mapping by impact type =====
const IMPACT_FEATURE_INDICES: Record<ImpactType, number[]> = {
  fuel_denial:          [0, 3, 12, 13],    // sorties, flight hours, scheduled missions
  isr_gap:              [1, 2, 5, 14],     // missions effective, ISR hours, objectives, effectiveness
  c2_degradation:       [1, 5, 14, 15],    // missions, objectives, effectiveness, capacity
  logistics_disruption: [8, 11, 18],       // munitions, uncommitted, depth ratio
  kill_chain_break:     [0, 1, 5, 14],     // sorties, missions, objectives, effectiveness
  comms_degradation:    [2, 14, 15, 19],   // ISR, effectiveness, capacity, schedule density
  defensive_gap:        [6, 11, 15],       // available, uncommitted, net capacity
  maintenance_cascade:  [7, 6, 15],        // maintenance, available, net capacity
}

// ===== Pattern type to impact type mapping =====
function patternToImpact(patternType: string, lostAssetRole: string): ImpactType {
  const role = lostAssetRole.toLowerCase()
  if (role.includes('tanker') || role.includes('refuel')) return 'fuel_denial'
  if (role.includes('isr') || role.includes('reaper') || role.includes('hawk')) return 'isr_gap'
  if (role.includes('awacs') || role.includes('c2') || role.includes('airborne')) return 'c2_degradation'
  if (role.includes('airlift') || role.includes('galaxy') || role.includes('globemaster')) return 'logistics_disruption'
  if (role.includes('satcom') || role.includes('comms')) return 'comms_degradation'

  switch (patternType) {
    case 'operational_dependency': return 'fuel_denial'
    case 'kill_chain_dependency': return 'kill_chain_break'
    case 'logistics_enablement': return 'logistics_disruption'
    case 'fleet_aging': return 'maintenance_cascade'
    case 'maintenance_cascade': return 'maintenance_cascade'
    case 'ace_dispersal': return 'comms_degradation'
    default: return 'defensive_gap'
  }
}

// ===== Cascade Edge =====
export interface CascadeEdge {
  fromAsset: string
  fromRole: string
  toAsset: string
  toRole: string
  impactType: ImpactType
  r: number           // correlation coefficient
  depth: number       // 1 = direct, 2 = secondary
  clusterId: string
}

// ===== Cascade Result per asset =====
export interface AssetCascadeResult {
  assetId: string
  assetType: string
  domain: string
  role: string
  isLost: boolean
  baselineCapacity: number
  capacityByTime: Record<TimePoint, number>
  degradationByTime: Record<TimePoint, number>  // 0-1, 0 = no degradation
  status: 'NOMINAL' | 'DEGRADED' | 'SEVERELY_DEGRADED' | 'MISSION_DENIED'
  impactChains: CascadeEdge[]
}

// ===== Full cascade result =====
export interface CascadeResult {
  lostAssets: Record<string, number>  // asset ID → count lost
  assets: AssetCascadeResult[]
  summary: {
    assetsLost: number       // number of asset types
    totalUnitsLost: number   // total units across all types
    assetsImpacted: number
    avgCapacityDelta: number
    threatEscalations: number
    missionsDenied: number
  }
}

// ===== Build dependency graph =====
export function buildDependencyGraph(
  clusters: FusionCluster[],
  lostAssetTypes: string[],
  assetDetails: Record<string, AssetDetail>,
  lossRatios?: Record<string, number>,  // asset_type → 0.0–1.0 (partial attrition)
): CascadeEdge[] {
  const edges: CascadeEdge[] = []
  const lostSet = new Set(lostAssetTypes)

  for (const cluster of clusters) {
    // Check if any lost asset is in this cluster
    const clusterLostAssets = cluster.assets.filter(a => lostSet.has(a))
    if (clusterLostAssets.length === 0) continue

    // For each lost asset, create edges to all other assets in the cluster
    for (const lostAsset of clusterLostAssets) {
      const lostDetail = Object.values(assetDetails).find(d => d.asset_type === lostAsset)
      const lostRole = lostDetail?.role ?? ''
      // Scale edge strength by loss ratio (partial attrition = proportionally weaker cascade)
      const ratio = lossRatios?.[lostAsset] ?? 1.0

      for (const targetAsset of cluster.assets) {
        if (lostSet.has(targetAsset)) continue // skip other lost assets

        const targetDetail = Object.values(assetDetails).find(d => d.asset_type === targetAsset)
        if (!targetDetail) continue

        const impactType = patternToImpact(cluster.pattern_type, lostRole)

        edges.push({
          fromAsset: lostAsset,
          fromRole: lostRole,
          toAsset: targetAsset,
          toRole: targetDetail.role,
          impactType,
          r: cluster.correlation_strength * ratio,
          depth: 1,
          clusterId: cluster.id,
        })
      }
    }
  }

  // Secondary edges: assets impacted by direct edges that are in clusters with other assets
  const directImpacted = new Set(edges.map(e => e.toAsset))
  for (const cluster of clusters) {
    const impactedInCluster = cluster.assets.filter(a => directImpacted.has(a))
    if (impactedInCluster.length === 0) continue

    for (const impacted of impactedInCluster) {
      for (const target of cluster.assets) {
        if (target === impacted || lostSet.has(target) || directImpacted.has(target)) continue

        const targetDetail = Object.values(assetDetails).find(d => d.asset_type === target)
        if (!targetDetail) continue

        const parentEdge = edges.find(e => e.toAsset === impacted)
        if (!parentEdge) continue

        edges.push({
          fromAsset: impacted,
          fromRole: parentEdge.toRole,
          toAsset: target,
          toRole: targetDetail.role,
          impactType: parentEdge.impactType,
          r: parentEdge.r * cluster.correlation_strength * 0.6,
          depth: 2,
          clusterId: cluster.id,
        })
      }
    }
  }

  return edges
}

// ===== Compute degradation for a feature vector at a time point =====
export function applyDegradationToVector(
  features: number[],
  edges: CascadeEdge[],
  timePoint: TimePoint,
): number[] {
  const modified = [...features]

  for (const edge of edges) {
    const curve = DEGRADATION_CURVES[edge.impactType]
    const baseMult = curve[timePoint]
    // Effective multiplier: 1 - (1 - base_mult) * r
    const effectiveMult = 1 - (1 - baseMult) * edge.r

    const indices = IMPACT_FEATURE_INDICES[edge.impactType]
    for (const idx of indices) {
      if (idx < modified.length) {
        modified[idx] = modified[idx] * effectiveMult
      }
    }
  }

  return modified.map(v => Math.round(v * 10000) / 10000)
}

// ===== Compute cascade curves for all time points =====
export function computeCascadeCurves(
  edges: CascadeEdge[],
  baselineFeatures: number[],
): Record<TimePoint, number[]> {
  const result: Record<number, number[]> = {}
  for (const tp of TIME_POINTS) {
    const assetEdges = edges
    result[tp] = applyDegradationToVector(baselineFeatures, assetEdges, tp)
  }
  return result as Record<TimePoint, number[]>
}

// ===== Get degradation status from capacity delta =====
export function getDegradationStatus(
  baselineCapacity: number,
  currentCapacity: number,
): 'NOMINAL' | 'DEGRADED' | 'SEVERELY_DEGRADED' | 'MISSION_DENIED' {
  if (baselineCapacity <= 0) return 'MISSION_DENIED'
  const ratio = currentCapacity / baselineCapacity
  if (ratio >= 0.85) return 'NOMINAL'
  if (ratio >= 0.55) return 'DEGRADED'
  if (ratio >= 0.25) return 'SEVERELY_DEGRADED'
  return 'MISSION_DENIED'
}
